import { motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import DemoForm from "../components/DemoForm";
import Footer from "../components/Footer";

// ── Data ──────────────────────────────────────────────────────────────────────

const CHECKLIST = [
  "4 PDP images per style",
  "Multiple mood backgrounds",
  "Automated brand audit",
  "2–3 day turnaround",
];

const HERO_STATS = [
  { value: "76%", label: "Cost reduction vs. traditional" },
  { value: "72 hrs", label: "Brief to approved delivery" },
  { value: "₹18 Cr", label: "Maximum annual saving" },
  { value: "Month 1", label: "Payback period" },
];

const PROBLEMS = [
  {
    value: "₹675",
    unit: "/style",
    label: "Traditional PDP cost",
    sub: "Before mood. Before rework. Before your next season.",
  },
  {
    value: "6–8",
    unit: " weeks",
    label: "Studio-to-delivery lead time",
    sub: "Per brand — all queuing for the same studios at peak season.",
  },
  {
    value: "₹1,200",
    unit: "/style",
    label: "Mood imagery cost",
    sub: "Separate shoot. Separate vendor. Most brands skip it entirely.",
  },
  {
    value: "4–6",
    unit: "/year",
    label: "Launch windows missed",
    sub: "Because imagery wasn't ready when the collection was.",
  },
];

const PROCESS_STEPS = [
  { label: "Brief", automated: false },
  { label: "Generate", automated: true },
  { label: "Audit", automated: false },
];

const PDP_IMAGES = [
  { url: "https://images.pexels.com/photos/11671964/pexels-photo-11671964.jpeg?auto=compress&cs=tinysrgb&w=400", label: "PDP 1" },
  { url: "https://images.pexels.com/photos/17938771/pexels-photo-17938771.jpeg?auto=compress&cs=tinysrgb&w=400", label: "PDP 2" },
  { url: "https://images.unsplash.com/photo-1779378106780-3016bc3c7915?w=400&q=80", label: "PDP 3" },
  { url: "https://images.unsplash.com/photo-1625860191460-10a66c7384fb?w=400&q=80", label: "PDP 4" },
];

const GALLERY_IMAGES = [
  { url: "https://images.unsplash.com/photo-1762843352680-21a700cb56ac?w=600&q=80", label: "Campaign Visual" },
  { url: "https://images.pexels.com/photos/11671964/pexels-photo-11671964.jpeg?auto=compress&cs=tinysrgb&w=600", label: "PDP Imagery" },
  { url: "https://images.unsplash.com/photo-1617690032354-34273b431955?w=600&q=80", label: "Campaign Visual" },
  { url: "https://images.unsplash.com/photo-1589363358751-ab05797e5629?w=600&q=80", label: "Product Shot" },
  { url: "https://images.unsplash.com/photo-1652473291442-7a2e034a00d1?w=600&q=80", label: "Lifestyle Scene" },
  { url: "https://images.pexels.com/photos/17938771/pexels-photo-17938771.jpeg?auto=compress&cs=tinysrgb&w=600", label: "Product Shot" },
  { url: "https://images.unsplash.com/photo-1664515226058-03952a19bd76?w=600&q=80", label: "Editorial" },
  { url: "https://images.unsplash.com/photo-1549439602-43ebca2327af?w=600&q=80", label: "Product Shot" },
  { url: "https://images.unsplash.com/photo-1534404483017-8743b4e935cd?w=600&q=80", label: "GTM Story" },
  { url: "https://images.pexels.com/photos/9150031/pexels-photo-9150031.jpeg?auto=compress&cs=tinysrgb&w=600", label: "Campaign Visual" },
  { url: "https://images.unsplash.com/photo-1516575150278-77136aed6920?w=600&q=80", label: "Campaign Visual" },
  { url: "https://images.pexels.com/photos/33856212/pexels-photo-33856212.jpeg?auto=compress&cs=tinysrgb&w=600", label: "Product Shot" },
  { url: "https://images.unsplash.com/photo-1616847220575-31b062a4cd05?w=600&q=80", label: "Editorial" },
  { url: "https://images.unsplash.com/photo-1779378106780-3016bc3c7915?w=600&q=80", label: "PDP Imagery" },
  { url: "https://images.pexels.com/photos/33367852/pexels-photo-33367852.jpeg?auto=compress&cs=tinysrgb&w=600", label: "Editorial" },
  { url: "https://images.unsplash.com/photo-1506152983158-b4a74a01c721?w=600&q=80", label: "Lifestyle Scene" },
  { url: "https://images.unsplash.com/photo-1601324389523-cb9bd3853025?w=600&q=80", label: "Lifestyle Scene" },
  { url: "https://images.unsplash.com/photo-1629511565591-a1d494ad6c58?w=600&q=80", label: "Editorial" },
  { url: "https://images.unsplash.com/photo-1732209989247-8669121056a9?w=600&q=80", label: "Campaign Visual" },
  { url: "https://images.pexels.com/photos/8221229/pexels-photo-8221229.jpeg?auto=compress&cs=tinysrgb&w=600", label: "Campaign Visual" },
  { url: "https://images.pexels.com/photos/37962706/pexels-photo-37962706.jpeg?auto=compress&cs=tinysrgb&w=600", label: "Editorial" },
  { url: "https://images.unsplash.com/photo-1519748771451-a94c596fad67?w=600&q=80", label: "GTM Story" },
  { url: "https://images.pexels.com/photos/31792947/pexels-photo-31792947.jpeg?auto=compress&cs=tinysrgb&w=600", label: "GTM Story" },
  { url: "https://images.unsplash.com/photo-1763069228076-c7e3995e1769?w=600&q=80", label: "GTM Story" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const scrollToContact = (e) => {
  e.preventDefault();
  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
};

// ── Page ──────────────────────────────────────────────────────────────────────

const GenAIPage = () => (
  <div data-testid="gen-ai-page">
    <Navbar />

    {/* ── Hero ──────────────────────────────────────────────────────────── */}
    <section className="min-h-screen bg-[#E8380D] flex items-center pt-20">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-20 w-full">
        {/* Back */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} className="mb-12">
          <Link to="/" data-testid="back-to-home" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest font-bold text-black/55 hover:text-black transition-colors">
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </motion.div>

        <div className="flex flex-col lg:flex-row lg:items-start lg:gap-20">
          {/* Left — copy */}
          <div className="flex-1 max-w-2xl">
            <motion.p custom={0} variants={fadeUp} initial="hidden" animate="visible"
              className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-black/55 mb-5">
              Your Catalogue.
            </motion.p>

            <motion.h1 custom={1} variants={fadeUp} initial="hidden" animate="visible"
              data-testid="gen-ai-hero-heading"
              className="font-cabinet font-black uppercase tracking-tighter leading-none text-[#0A0A0A] mb-4"
              style={{ fontSize: "clamp(3rem, 7vw, 7rem)" }}>
              PDP + Mood.
              <br />
              <span className="text-[#0A0A0A]">No Studio.</span>
            </motion.h1>

            <motion.p custom={2} variants={fadeUp} initial="hidden" animate="visible"
              className="font-cabinet font-black uppercase text-2xl md:text-3xl text-[#0A0A0A]/70 mb-6 tracking-tight">
              72 Hours.
            </motion.p>

            <motion.p custom={3} variants={fadeUp} initial="hidden" animate="visible"
              className="font-manrope text-base md:text-lg text-black/70 leading-relaxed mb-10 max-w-xl">
              Multi-brand retail groups replace their entire photography pipeline with Merai — and save up to ₹18 Cr a year from month one.
            </motion.p>

            {/* CTAs */}
            <motion.div custom={4} variants={fadeUp} initial="hidden" animate="visible" className="flex flex-wrap gap-4 mb-10">
              <a href="#contact" data-testid="gen-ai-hero-cta" onClick={scrollToContact}
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#00FF66] font-cabinet font-black uppercase text-sm tracking-widest px-8 py-4 hover:bg-[#00FF66] hover:text-[#0A0A0A] transition-colors duration-200">
                Book a 30-Min Demo →
              </a>
              <a href="#gallery" data-testid="gen-ai-sample-cta"
                onClick={(e) => { e.preventDefault(); document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth" }); }}
                className="inline-flex items-center gap-2 border-2 border-[#0A0A0A] text-[#0A0A0A] font-cabinet font-black uppercase text-sm tracking-widest px-8 py-4 hover:bg-[#0A0A0A] hover:text-[#00FF66] transition-colors duration-200">
                See Sample Output
              </a>
            </motion.div>

            {/* Checklist */}
            <motion.ul custom={5} variants={fadeUp} initial="hidden" animate="visible" className="space-y-2.5">
              {CHECKLIST.map((item) => (
                <li key={item} className="flex items-center gap-3 font-manrope text-sm font-semibold text-black/75">
                  <span className="w-5 h-5 bg-[#00FF66] flex items-center justify-center flex-shrink-0">
                    <Check size={12} strokeWidth={3} className="text-[#0A0A0A]" />
                  </span>
                  {item}
                </li>
              ))}
            </motion.ul>
          </div>

          {/* Right — stats 2×2 */}
          <motion.div custom={3} variants={fadeUp} initial="hidden" animate="visible"
            className="mt-16 lg:mt-0 lg:w-80 xl:w-96 grid grid-cols-2 border-2 border-[#0A0A0A]/20 flex-shrink-0">
            {HERO_STATS.map((stat, i) => (
              <div key={stat.label}
                className={`p-6 xl:p-8 ${i % 2 === 0 ? "border-r-2" : ""} ${i < 2 ? "border-b-2" : ""} border-[#0A0A0A]/20`}>
                <div className="font-cabinet text-3xl xl:text-4xl font-black text-[#0A0A0A] leading-none mb-2">
                  {stat.value}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-black/55 leading-snug">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>

    {/* ── The Problem ───────────────────────────────────────────────────── */}
    <section className="bg-[#0A0A0A] py-24 md:py-36">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16 max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/30 mb-4">The Problem</p>
          <h2 className="font-cabinet text-4xl sm:text-5xl font-black uppercase tracking-tight text-[#F4F4F0] leading-tight mb-5">
            The model compounds cost.
            <br />
            <span className="text-white/40">Across brands, every season.</span>
          </h2>
          <p className="font-manrope text-base text-white/55 leading-relaxed">
            It's not one cost. It's PDP per style, plus mood per style, plus rework — multiplied across every brand, every season.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {PROBLEMS.map((p, i) => (
            <motion.div key={p.label} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-40px" }}
              className="bg-[#0A0A0A] p-8 xl:p-10">
              <div className="font-cabinet font-black text-[#00FF66] leading-none mb-1" style={{ fontSize: "clamp(2.5rem, 4vw, 3.5rem)" }}>
                {p.value}<span className="text-xl text-[#00FF66]/60">{p.unit}</span>
              </div>
              <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-3 mt-2">{p.label}</div>
              <p className="font-manrope text-sm text-white/35 leading-relaxed">{p.sub}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* ── How It Works ──────────────────────────────────────────────────── */}
    <section className="bg-[#E8380D] py-24 md:py-36">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-14">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-black/50 mb-4">How It Works</p>
          <h2 className="font-cabinet text-4xl sm:text-5xl font-black uppercase tracking-tight text-[#0A0A0A]">
            Three steps.
            <br />
            <span className="text-black/50">Two are automated.</span>
          </h2>
        </motion.div>

        {/* Step pills */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="flex flex-wrap items-center gap-3 mb-12">
          {PROCESS_STEPS.map((step, i) => (
            <div key={step.label} className="flex items-center gap-3">
              <div className={`relative font-cabinet font-black uppercase text-sm tracking-widest px-5 py-3 flex items-center gap-2
                ${step.automated ? "bg-[#0A0A0A] text-[#00FF66]" : "bg-[#0A0A0A]/10 text-[#0A0A0A]"}`}>
                {step.label}
                {step.automated && (
                  <span className="font-mono text-[9px] uppercase tracking-widest bg-[#00FF66] text-[#0A0A0A] px-1.5 py-0.5">
                    FULLY AUTOMATED
                  </span>
                )}
              </div>
              {i < PROCESS_STEPS.length - 1 && (
                <span className="font-cabinet font-black text-black/30 text-xl">→</span>
              )}
            </div>
          ))}
        </motion.div>

        {/* Feature card */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="bg-[#F4F4F0] p-8 md:p-12">
          <div className="flex flex-col lg:flex-row lg:gap-16">
            {/* Card copy */}
            <div className="lg:w-2/5 mb-10 lg:mb-0 flex flex-col justify-between">
              <div>
                <h3 className="font-cabinet text-2xl md:text-3xl font-black uppercase text-[#0A0A0A] mb-4 tracking-tight">
                  PDP and mood.
                  <br />
                  Same pipeline.
                </h3>
                <p className="font-manrope text-sm md:text-base text-[#4A4A4A] leading-relaxed mb-8">
                  Four clean PDP images, marketplace-ready for Amazon, Myntra, Flipkart — plus multiple mood backgrounds for campaign, social, and seasonal. Generated simultaneously. No studio. No models. No logistics.
                </p>
              </div>
              <a href="#contact" onClick={scrollToContact}
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest font-bold text-[#0A0A0A] border-b-2 border-[#0A0A0A] pb-1 hover:text-[#E8380D] hover:border-[#E8380D] transition-colors self-start">
                See this in a live demo →
              </a>
            </div>

            {/* PDP grid */}
            <div className="lg:w-3/5">
              <div className="grid grid-cols-4 gap-2 mb-3">
                {PDP_IMAGES.map((img) => (
                  <div key={img.label} className="relative aspect-[3/4] overflow-hidden bg-[#E8E8E4]">
                    <img src={img.url} alt={img.label} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute bottom-0 left-0 right-0 bg-[#0A0A0A]/70 py-1 px-2">
                      <span className="font-mono text-[9px] text-white/80 uppercase tracking-wider">{img.label}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]/45">
                + Mood backgrounds — same pipeline, zero extra shoots
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>

    {/* ── Sample Output Gallery ─────────────────────────────────────────── */}
    <section id="gallery" className="bg-[#0A0A0A] py-20 md:py-28" data-testid="image-gallery-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/30 mb-3">Sample Output</p>
          <h2 className="font-cabinet text-4xl sm:text-5xl font-black uppercase tracking-tight text-[#F4F4F0]">
            WHAT MERAI CREATES
          </h2>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.8 }} viewport={{ once: true }}
          className="columns-2 sm:columns-3 md:columns-4 gap-3">
          {GALLERY_IMAGES.map((item, i) => (
            <div key={i} className="break-inside-avoid mb-3 overflow-hidden group relative cursor-default">
              <img src={item.url} alt={item.label} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-[#0A0A0A]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#00FF66] font-bold">{item.label}</span>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>

    {/* ── Demo Form ─────────────────────────────────────────────────────── */}
    <DemoForm />
    <Footer />
  </div>
);

export default GenAIPage;
